import { useEffect, useState } from "react";
import { NavLink, Outlet } from "react-router-dom";
import { LayoutDashboard, FolderOpen, CalendarDays, Users, Sparkles, LogOut, CheckCircle2, BarChart3, Home } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { useClient } from "@/context/ClientContext";
import { api } from "@/lib/api";
import AgentPanel from "@/components/AgentPanel";
import NotificationBell from "@/components/NotificationBell";
import ClientLogo from "@/components/ClientLogo";
import InstallBanner from "@/components/InstallBanner";
import UploadPanel from "@/components/UploadPanel";
import { DragProvider } from "@/lib/dragMove";
import { DragOverlay } from "@/components/DragLayer";
import ThemeToggle from "@/components/ThemeToggle";

const ADMIN_NAV = [
  { to: "/", label: "Übersicht", icon: LayoutDashboard, id: "nav-dashboard" },
  { to: "/freigaben", label: "Freigaben", icon: CheckCircle2, id: "nav-approvals", badge: true },
  { to: "/kalender", label: "Kalender", icon: CalendarDays, id: "nav-calendar" },
  { to: "/drive", label: "Drive", icon: FolderOpen, id: "nav-drive" },
  { to: "/analytik", label: "Analytik", icon: BarChart3, id: "nav-analytics" },
  { to: "/kunden", label: "Kunden", icon: Users, id: "nav-clients" },
];
const CLIENT_NAV = [
  { to: "/", label: "Freigabe", icon: Home, id: "nav-dashboard", badge: true },
  { to: "/kalender", label: "Kalender", icon: CalendarDays, id: "nav-calendar" },
  { to: "/drive", label: "Drive", icon: FolderOpen, id: "nav-drive" },
];

function usePendingCount(clientId) {
  const [n, setN] = useState(0);
  useEffect(() => {
    if (!clientId) return;
    const load = () => api.get("/posts", { params: { client_id: clientId } }).then((r) => setN(r.data.filter((p) => p.status === "pending_approval").length)).catch(() => {});
    load();
    window.addEventListener("posts-changed", load);
    return () => window.removeEventListener("posts-changed", load);
  }, [clientId]);
  return n;
}

const Badge = ({ n, id }) => n > 0 ? <span className="text-[11px] font-semibold bg-amber-400 text-slate-950 rounded-full px-1.5 min-w-[18px] text-center" data-testid={id}>{n}</span> : null;

export default function AppShell() {
  const { user, logout } = useAuth();
  const { clients, clientId, setClientId, client } = useClient();
  const [agentOpen, setAgentOpen] = useState(false);
  const isAdmin = user.role === "admin";
  const pending = usePendingCount(clientId);
  const nav = isAdmin ? ADMIN_NAV : CLIENT_NAV;
  const mobileNav = isAdmin ? ADMIN_NAV.slice(0, 4).concat(ADMIN_NAV[5]) : CLIENT_NAV;
  useEffect(() => {
    const stop = (e) => e.preventDefault();
    window.addEventListener("dragover", stop); window.addEventListener("drop", stop);
    return () => { window.removeEventListener("dragover", stop); window.removeEventListener("drop", stop); };
  }, []);

  const clientPicker = isAdmin && (
    <Select value={clientId || ""} onValueChange={setClientId}>
      <SelectTrigger data-testid="client-select-dropdown"><SelectValue placeholder="Kunde wählen" /></SelectTrigger>
      <SelectContent>{clients.map((c) => <SelectItem key={c.id} value={c.id} data-testid={`client-option-${c.id}`}>{c.name}</SelectItem>)}</SelectContent>
    </Select>
  );

  return (
    <DragProvider>
    <div className="min-h-screen lg:flex">
      <aside className="hidden lg:flex w-64 shrink-0 border-r glass-strong flex-col p-5 sticky top-0 h-screen">
        <div className="px-2 mb-8 mt-1 flex items-start justify-between">
          <div>
            <p className="font-display font-bold text-lg leading-tight">Content <span className="text-teal-400">Management</span></p>
            <p className="font-mono text-[10px] tracking-[0.3em] uppercase text-slate-500 mt-1">CRE8 Studio</p>
          </div>
          <div className="flex items-center gap-1">
            <ThemeToggle testId="sidebar-theme-toggle" />
            {isAdmin && <NotificationBell />}
          </div>
        </div>
        {isAdmin ? (
          <div className="mb-6"><p className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground px-2 mb-2">Kunde</p>{clientPicker}</div>
        ) : (
          <div className="mb-6 px-2" data-testid="client-name-label">
            <p className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground mb-1">Kundenportal</p>
            <div className="mt-1"><ClientLogo client={client} className="h-7" /></div>
          </div>
        )}
        <nav className="space-y-1 flex-1">
          {nav.map(({ to, label, icon: Icon, id, badge }) => (
            <NavLink key={to} to={to} end data-testid={id}
              className={({ isActive }) => `flex items-center gap-3 px-3.5 py-2.5 rounded-xl text-sm font-medium transition-colors ${isActive ? "bg-white/10 text-white shadow-[inset_0_1px_0_rgba(255,255,255,0.08)] [&>svg]:text-teal-300" : "text-slate-400 hover:text-slate-100 hover:bg-white/5"}`}>
              <Icon className="h-4 w-4" />{label}{badge && <span className="ml-auto"><Badge n={pending} id="nav-pending-count" /></span>}
            </NavLink>
          ))}
        </nav>
        {isAdmin && (
          <Button onClick={() => setAgentOpen(true)} className="mb-4 bg-purple-600 hover:bg-purple-500 text-white justify-start gap-2" data-testid="open-agent-button"><Sparkles className="h-4 w-4" /> Agent fragen</Button>
        )}
        <div className="border-t border-border pt-4 px-2 flex items-center justify-between">
          <div className="min-w-0">
            <p className="text-sm truncate" data-testid="current-user-name">{user.name}</p>
            <p className="text-xs text-muted-foreground truncate">{user.email}</p>
          </div>
          <button onClick={logout} className="text-slate-400 hover:text-white" data-testid="logout-button" title="Abmelden"><LogOut className="h-4 w-4" /></button>
        </div>
      </aside>

      <header className="lg:hidden sticky top-0 z-30 glass border-b px-4 h-14 box-content pt-[env(safe-area-inset-top)] flex items-center gap-3" data-testid="mobile-header">
        <p className="font-display font-bold leading-tight shrink-0">Content <span className="text-teal-400">Mgmt</span></p>
        <div className="flex-1 min-w-0">{isAdmin ? clientPicker : <div className="flex justify-end" data-testid="mobile-client-name"><ClientLogo client={client} className="h-5" fallbackClass="text-sm text-slate-400 truncate" testId="mobile-client-logo" /></div>}</div>
        <ThemeToggle testId="mobile-theme-toggle" className="shrink-0" />
        {isAdmin && <NotificationBell testId="mobile-notification-bell" />}
        {isAdmin && <button onClick={() => setAgentOpen(true)} className="h-9 w-9 grid place-items-center rounded-lg bg-purple-600" data-testid="mobile-agent-button"><Sparkles className="h-4 w-4" /></button>}
        <button onClick={logout} className="h-9 w-9 grid place-items-center text-slate-400" data-testid="mobile-logout-button"><LogOut className="h-4 w-4" /></button>
      </header>

      <main className="flex-1 min-w-0 px-4 sm:px-8 lg:px-12 py-6 lg:py-10 pb-28 lg:pb-10">
        {clientId ? <><InstallBanner /><Outlet /></> : <p className="text-muted-foreground" data-testid="no-client-hint">Lege zuerst unter „Kunden" einen Kunden an.</p>}
        {isAdmin && <UploadPanel />}
      </main>

      <nav className="lg:hidden fixed z-30 inset-x-3 bottom-[calc(0.75rem+env(safe-area-inset-bottom))] rounded-[28px] glass-strong border shadow-[0_18px_40px_-10px_rgba(0,0,0,0.55)] flex p-1.5" data-testid="mobile-bottom-nav">
        {mobileNav.map(({ to, label, icon: Icon, id, badge }) => (
          <NavLink key={to} to={to} end data-testid={`m-${id}`}
            className={({ isActive }) => `flex-1 flex flex-col items-center justify-center gap-0.5 h-14 rounded-[22px] text-[10px] font-medium relative transition-[background-color,color] duration-200 ${isActive ? "bg-white/10 text-teal-300" : "text-slate-400"}`}>
            <Icon className="h-5 w-5" />{label}
            {badge && <span className="absolute top-2 left-1/2 ml-2"><Badge n={pending} id={`m-${id}-badge`} /></span>}
          </NavLink>
        ))}
      </nav>
      {isAdmin && <AgentPanel open={agentOpen} onOpenChange={setAgentOpen} />}
      <DragOverlay />
    </div>
    </DragProvider>
  );
}
